import React, { useState, useEffect, useMemo } from 'react';
// libraries and components
import { useTranslation } from 'react-i18next';
import DialPad from '../DialPad/DialPad';
// assets
import { connectSVG, dialPadSVG, disconnectSVG, pauseCallIcon, transferCallIcon } from '../../../shared/helpers/icons';
// connection middlewares
import { onStart, onDisconnectAgent } from '../../middlewares/agentConnect';
// styles
import styles from './softphone.module.scss';
import useStore from '../../store/store';
import { Registerer, UserAgent } from 'sip.js';
import { onEndCall, onMakeCall } from '../../middlewares/agentCalling';
import { SessionState } from 'sip.js/lib/core';
import CallHandlerContainer from '../CallHandler/CallHandlerContainer';
import { onAcceptInvitation, onRejectInvitation, onHoldInvitation, onUnHoldInvitation, handleAttendedTransfer } from '../../middlewares/agenteReceiveInvitation';
import { callingSound, marimbaSound } from '../../../shared/helpers/sounds';
import Timer from '../Timer/Timer';
import useSound from 'use-sound';
import TooltipButton from '../../../shared/components/TooltipButton';

const DIAL_PAD_MODES = {
    DEFAULT: 'default',
    CALL: 'call',
    TRANSFER: 'transfer'
};

const TRANSFER_STEPS = {
    DEFAULT: 'default',
    INITIAL: 'initial',
    CONSULTING: 'consulting',
    COMPLETED: 'completed'
};

const SoftPhone = ({ username, credentials }) => {

    const { t } = useTranslation(['softphone']);
    const agentOnLine = useStore((state) => state.agent.onLine)

    const [openDialPad, setOpenDialPad] = useState(false);

    const agentRegistered = useStore((state) => state.agentRegistered);
    const agentUnRegistered = useStore((state) => state.agentUnRegistered);

    const invite = useStore((state) => state.invite);

    const invitation = useStore((state) => state.invitation);
    const receiveInvitation = useStore((state) => state.receiveInvitation);
    const acceptInvitation = useStore((state) => state.acceptInvitation);
    const endInvitation = useStore((state) => state.endInvitation);

    const call = useStore((state) => state.call);
    const endCall = useStore((state) => state.endCall);

    const reject = useStore((state) => state.reject);
    const end = useStore((state) => state.end);
    const cancel = useStore((state) => state.cancel);

    const outgoingCall = useStore((state) => state.outgoingCall);
    const endOutgoing = useStore((state) => state.endOutgoing);
    const endOutgoingCall = useStore((state) => state.endOutgoingCall);
    const outgoingCallAccepted = useStore((state) => state.outgoingCallAccepted);
    const [hold, setHold] = useState(false);

    let transportOptions = null;
    let uri = null;
    let user_agent_options = null;
    const [userAgent, setUserAgent] = useState(null);

    const [registerer, setRegisterer] = useState(null);

    const remoteStream = new MediaStream();

    const [isCalling, setIsCalling] = useState(false);
    const [callingNumber, setCallingNumber] = useState('');

    const [shown, setShown] = useState(null);
    const [thereIsCall, setThereIsCall] = useState(null);

    const [playReceiving, { stop: stopReceiving }] = useSound(marimbaSound);

    const [playCalling, { stop: stopCalling }] = useSound(callingSound, {
        loop: true,
        interrupt: true
    });

    // Estados para transferencia
    const [dialPadMode, setDialPadMode] = useState(DIAL_PAD_MODES.DEFAULT);
    
    const [transferStep, setTransferStep] = useState(TRANSFER_STEPS.DEFAULT);

    // Memo para la sesión activa
    const activeSession = useMemo(() => {
        if (call.thereIs) return call;

        if (outgoingCall.thereIs) return outgoingCall;
        
        return null;
    }, [call, outgoingCall]);

    // Memo para verificar si hay una sesión activa
    const hasActiveSession = useMemo(() => {
        return Boolean(activeSession);
    }, [activeSession]);

    // Memo para verificar si está en proceso de transferencia
    const isTransferring = useMemo(() => {
        return transferStep === TRANSFER_STEPS.CONSULTING;
    }, [transferStep]);

    useEffect(() => {
        if (invitation.thereIs) {
            playReceiving();
        };
        if (!invitation.thereIs) {
            stopReceiving();
        };
    }, [outgoingCall.thereIs, invitation.thereIs])

    useEffect(() => {
        if (isCalling) {
            playCalling();
        } else {
            stopCalling();
        }
    }, [isCalling]);

    useEffect(() => {
        if (call.thereIs || (outgoingCall.thereIs && outgoingCall.accepted)) {
            stopCalling();
            stopReceiving();
            setIsCalling(false);
        }
    }, [call.thereIs, outgoingCall.thereIs, outgoingCall.accepted]);

    useEffect(() => {
        setThereIsCall((call && call.thereIs) || (outgoingCall && outgoingCall.thereIs) || (invitation && invitation.thereIs))
    }, [outgoingCall.data, call.data, invitation.data]);

    useEffect(() => {
        switch (thereIsCall) {
            case true:
                setShown(true);
                break;
            case false:
                setShown(false);
                break;
            case null:
                setShown(null);
                break;
            default:
                break;
        };
    }, [thereIsCall])

    async function setupRemoteMedia(session) {
        const audio = document.createElement('audio');
        audio.controls = true;
        audio.autoplay = true;
        session.sessionDescriptionHandler.peerConnection.getReceivers().forEach((receiver) => {
            if (receiver.track) {
                remoteStream.addTrack(receiver.track);
            }
        });
        audio.srcObject = remoteStream;
        audio.play();
    }

    function cleanupMedia() {
        let audio = document.getElementsByTagName('audio');
        audio.srcObject = null;
        audio.audio.pause();
    }

    useEffect(() => {
        return () => {
            if (registerer) {
                handleDisconnect();
            }
        };
    }, [registerer]);
    
    function onInvite(invitation) {

        invitation.stateChange.addListener((state: SessionState) => {
            switch (state) {
                case SessionState.Initial:
                    break;
                case 'Establishing':
                    break;
                //case SessionState.Established:
                case 'Established':
                    setupRemoteMedia(invitation);
                    break;
                case 'Terminating':
                // fall through
                case SessionState.Terminated:
                    cleanupMedia();
                    cancel();
                    break;
                default:
                    console.log("Unknown state")
            }
        });
        receiveInvitation(invitation)
    };

    useEffect(() => {
        if (registerer) {
            handleConnectAgent();
        }
    }, [registerer, userAgent]);

    const handleConnectAgent = () => {
        onStart(registerer, userAgent, agentRegistered, agentUnRegistered);
    }

    const handleMakeCall = (number) => {
        setCallingNumber(number);
        onMakeCall(userAgent, `sip:${number}@${credentials.domain}`, setupRemoteMedia, cleanupMedia, invite, outgoingCallAccepted, handleCloseDialPad, endOutgoing);
    }

    const handleAcceptCall = () => {
        onAcceptInvitation(invitation.data, acceptInvitation, end, cleanupMedia);
    }

    const resetHoldState = () => {
        if (!hold) return;
        setHold(false);        
    };

    const handleRejectCall = () => {
        onRejectInvitation(invitation.data, reject, cleanupMedia);
        resetHoldState();
    }

    const handleEndCall = () => {
        onEndCall(call.data, end);
        resetHoldState();
    }

    const handleEndOutgoingCall = () => {
        onEndCall(outgoingCall.data, endOutgoing, cleanupMedia);
        resetHoldState();
    }

    const handleDisconnect = () => {
        onDisconnectAgent(registerer, agentUnRegistered);
    }

    const handleCloseDialPad = () => {
        setOpenDialPad(false);
        setDialPadMode(DIAL_PAD_MODES.DEFAULT);
    }

    const handleOpenDialPad = () => {
        setOpenDialPad(true);
        setDialPadMode(DIAL_PAD_MODES.CALL);
    }

    /**
     * Handles the hold/unhold functionality for an active call.
     * This function manages both incoming and outgoing calls,
     * allowing to put them on hold or resume them.
     * 
     * @async
     * @function handleHoldCall
     * @description
     * - Determines the active session (either incoming or outgoing call)
     * - If no active session exists, returns early
     * - If call is not on hold, puts it on hold
     * - If call is on hold, resumes it
     * - Updates the hold state accordingly
     * 
     * @throws {Error} If the hold/unhold operation fails
     * @returns {Promise<void>}
     */
    const handleHoldCall = async () => {   
        
        if (!activeSession) {
            return;
        }
       
        if (!hold) {
            await onHoldInvitation(activeSession);
            setHold(true);
            return;
        } 

        await onUnHoldInvitation(activeSession);
        setHold(false);
    }

    

    // Función para manejar el click en transferir
    const handleTransferClick = () => {
        
        if (!hasActiveSession) return;
        
        if(dialPadMode === DIAL_PAD_MODES.TRANSFER) {
            setDialPadMode(DIAL_PAD_MODES.DEFAULT);
            setOpenDialPad(false); 
            setTransferStep(TRANSFER_STEPS.DEFAULT);
            return;
        }
        setDialPadMode(DIAL_PAD_MODES.TRANSFER);
        setOpenDialPad(true);
        setTransferStep(TRANSFER_STEPS.INITIAL);
    };

    // Función para manejar la acción del DialPad
    const handleDialPadAction = async (number) => {
        if (dialPadMode === DIAL_PAD_MODES.TRANSFER) {
             
            if (!activeSession || !number) return;

            
                setTransferStep(TRANSFER_STEPS.CONSULTING);
                
                await handleAttendedTransfer(
                    activeSession,
                    number,
                    credentials.domain
                );
                
                setOpenDialPad(false);
                setTransferStep(TRANSFER_STEPS.DEFAULT);
                setDialPadMode(DIAL_PAD_MODES.DEFAULT);
                end();
                endOutgoing();
                
            return
        } 
        
        if(dialPadMode === DIAL_PAD_MODES.CALL) {
            setIsCalling(true); 
            onMakeCall(userAgent, `sip:${number}@${credentials.domain}`, setupRemoteMedia, cleanupMedia, invite, handleOutgoingCallAccepted, handleCloseDialPad, endOutgoing);
        }
    };

    const handleOutgoingCallAccepted = () => {
        stopCalling();
        setIsCalling(false);
        outgoingCallAccepted();
    };

     

    useEffect(() => {
        if (credentials && Object.keys(credentials).length > 0) {
            transportOptions = {
                server: `wss://${credentials.domain}:8099/ws`,
                raceSip: true
            };
            uri = UserAgent.makeURI(`sip:${credentials.user}@${credentials.domain}`);
            user_agent_options = {
                authorizationPassword: credentials.password,
                authorizationUsername: credentials.user,
                transportOptions,
                uri,
                userAgentString: 'omuni-softphone',
                displayName: credentials.user,
                contactParams: {
                    name: credentials.user
                },
                delegate: {
                    onInvite,
                },
                // this could be a parameter, but given that we will only be working with denwa webRTC for now, this must stay 
                // as a constant
                userAgentString: "Denwa WebRTC Phone",
                hackWssInTransport: true,
                viaHost: credentials.domain
            };
            let usagent = new UserAgent(user_agent_options);
            setUserAgent(usagent);
            setRegisterer(new Registerer(usagent));
        }
    }, [credentials]);

    useEffect(() => {
        if (invitation.rejected) {
            setTimeout(() => {
                endInvitation();
                resetHoldState();
            }, 2000);
        }
    }, [invitation.rejected]);

    useEffect(() => {
        if (invitation.cancelled) {
            setTimeout(() => {
                endInvitation();
                resetHoldState();
            }, 2000);
        }
    }, [invitation.cancelled]);

    useEffect(() => {
        if (call.ended) {
            setTimeout(() => {
                endCall();
                resetHoldState();
                if (isCalling) {
                    setIsCalling(false);
                }
            }, 2000);
        }
    }, [call.ended]);

    useEffect(() => {
        if (outgoingCall.ended) {
            setTimeout(() => {
                endOutgoingCall();
                resetHoldState();
                if (isCalling) {
                    setIsCalling(false);
                    setCallingNumber('');
                }
            }, 2000);
        };
    }, [outgoingCall.ended]);

    return (
        <div className={styles.softphoneContainer}>
            <div className={styles.main}>
                <div className={styles.agent}>
                    <div className={styles.connectedAgent}>
                        <div className={`${styles.stateColor} ${agentOnLine ? styles.connected : styles.disconnected}`} />
                        <>{agentOnLine ? t('softphone:connected') : t('softphone:disconnected')}</>
                    </div>
                    <div className={styles.barButtons}>
                        <TooltipButton
                            title={agentOnLine ? t('softphone:disconnect') : t('softphone:connect')}
                            icon={agentOnLine ? disconnectSVG : connectSVG}
                            onClick={agentOnLine ? handleDisconnect : handleConnectAgent}
                        />
                        <div className={`${styles.buttonWrapper} 
                            ${dialPadMode === DIAL_PAD_MODES.TRANSFER ? styles.selected : ''} 
                            ${!hasActiveSession ? styles.disabled : ''}`}>
                            <TooltipButton
                                title={t('softphone:transfer_call')}
                                icon={transferCallIcon}
                                onClick={handleTransferClick}
                                disabled={!hasActiveSession || isTransferring}
                            />
                        </div>
                        <div className={`${styles.buttonWrapper} 
                            ${hold ? styles.selected : ''} 
                            ${!hasActiveSession ? styles.disabled : ''}`}>
                            <TooltipButton
                                title={t('softphone:pause_call')}
                                icon={pauseCallIcon}
                                onClick={handleHoldCall}
                                disabled={!hasActiveSession}
                            />
                        </div>
                        <div className={`${styles.buttonWrapper} ${dialPadMode === DIAL_PAD_MODES.CALL ? styles.selected : ''}`}>
                            <TooltipButton
                                title={!openDialPad && agentOnLine ? t('softphone:open_dial_pad') : agentOnLine ? t('softphone:close_dial_pad') : ''}
                                icon={dialPadSVG}
                                onClick={!openDialPad ? handleOpenDialPad : handleCloseDialPad}
                                disabled={!agentOnLine}
                            />
                        </div>
                    </div>
                    <div className={styles.agentInfo}>
                        <div className={styles.agentCalls}>
                        </div>
                        <div className={styles.agentData}>
                            <span>{`${username}_${credentials.user}`}</span>
                            <div className={styles.timeInfo}>
                                <span className={styles.agentData}>{`${credentials.user} -`}&nbsp;</span>
                                {
                                    agentOnLine &&
                                    <Timer />
                                }
                            </div>
                        </div>
                    </div>
                </div>

            </div>
            {openDialPad &&
                <div className={styles.dialPadContainer}>
                    <DialPad
                        handleMakeCall={handleDialPadAction}
                        isCalling={isCalling}
                        setIsCalling={setIsCalling}
                    />
                </div>
            }
            <div className={shown === null ? styles.initialOut : shown === false ? styles.popupOut : styles.popupIn}>
                <CallHandlerContainer
                    handleEndCall={handleEndCall}
                    handleEndOutgoingCall={handleEndOutgoingCall}
                    call={call}
                    outgoingCall={outgoingCall}
                    callingNumber={callingNumber}
                    invitation={invitation}
                    handleAcceptCall={handleAcceptCall}
                    handleRejectCall={handleRejectCall}
                />
            </div>
            <audio id='audio'></audio>
        </div>
    )
};

export default SoftPhone;